/**
 * Main diagram system module that composes all diagram-related functionality
 * Provides a unified API for diagram rendering and pan/zoom functionality
 */
import { resolveDependencies, createEventManager } from './dependency-resolver.js';
import { createDiagramRenderer } from './diagram-renderer.js';
import { createPanZoomController } from './pan-zoom-controller.js';
import lockedNodeRenderer from './locked-node-renderer.js';

/**
 * Creates the diagram system with all sub-modules
 * @param {Object} options - Configuration options
 * @returns {Object} - Complete diagram system
 */
export function createDiagramSystem(options = {}) {
  const { asyncUtils, eventBus, stateManager } = resolveDependencies(options);
  const events = createEventManager(eventBus);
  
  // Create sub-modules
  const renderer = createDiagramRenderer(options);
  const panZoom = createPanZoomController(options);
  
  /**
   * Render a diagram in the given container with the provided data
   * @param {HTMLElement|string} container - The container element or its ID
   * @param {string} diagramData - The Mermaid diagram definition
   * @returns {Promise} - Promise that resolves when rendering is complete
   */
  async function renderDiagram(container, diagramData) {
    console.log('renderDiagram called (renamed from onJourneyTabShown)');
    
    // Handle container parameter (can be string ID or element)
    let containerElement = container;
    if (typeof container === 'string') {
      containerElement = document.getElementById(container);
    }
    
    // Validate that a container was provided - no fallbacks
    if (!containerElement) {
      const error = new Error('No diagram container provided - container parameter is required');
      console.error(error.message);
      events.emit('diagram:error', { error });
      return Promise.reject(error);
    }
    
    // Use provided data or fallback to window global
    const mermaidData = diagramData || window.journeyDiagramData;
    if (!mermaidData) {
      const error = new Error('No diagram data provided');
      console.error(error.message);
      events.emit('diagram:error', { error });
      return Promise.reject(error);
    }
    
    try {
      // Check if the SVG already exists
      const existingSvg = containerElement.querySelector('svg');
      if (!existingSvg) {
        // No SVG found, render the diagram
        events.emit('diagram:loadStart');
        
        try {
          // Log start time for rendering
          const renderStartTime = performance.now();
          
          // Render the diagram
          const renderedDiagramDiv = await renderer.renderMermaidDiagram(
            containerElement, 
            mermaidData
          );
          
          // Calculate and log rendering time
          const renderEndTime = performance.now();
          console.log(`Mermaid diagram rendering took ${(renderEndTime - renderStartTime).toFixed(2)}ms`);
          
          // Initialize pan-zoom controls
          panZoom.initializeDiagramControls();
          
          // Apply locked node styles
          if (typeof lockedNodeRenderer.setupNodeStyles === 'function') {
            lockedNodeRenderer.setupNodeStyles();
          }
          
          // Style locked nodes for all browsers
          if (typeof lockedNodeRenderer.styleLockedNodes === 'function') {
            // Apply styling for locked nodes in all browsers
            console.log('[DEBUG] Styling locked nodes for all browsers');
            lockedNodeRenderer.styleLockedNodes();
          }
          
          // Wait for next frame to ensure DOM updates
          await asyncUtils.nextFrame();
          
          // Notify successful render
          events.emit('diagram:loadComplete', { success: true });
          return renderedDiagramDiv;
        } catch (error) {
          console.error('Failed to render mermaid diagram:', error);
          
          // Clear any mermaid-generated error displays first
          const mermaidDiv = containerElement.querySelector('.mermaid');
          if (mermaidDiv) {
            mermaidDiv.innerHTML = ''; // This removes the mermaid error SVG
          }
          
          // Create and display error message
          const errorDiv = renderer.createErrorDisplay(error);
          containerElement.appendChild(errorDiv);
          
          // Notify error
          events.emit('diagram:loadComplete', { 
            success: false, 
            error: error 
          });
          
          return Promise.reject(error);
        }
      } else {
        // SVG exists but panzoom may not be initialized
        if (!window.panzoomStore || !window.panzoomStore.instance) {
          panZoom.initializeDiagramControls();
          events.emit('diagram:panzoomInitialized');
        }
        
        return Promise.resolve(existingSvg.parentElement);
      }
    } catch (error) {
      console.error('Error in renderDiagram:', error);
      events.emit('diagram:error', { error });
      return Promise.reject(error);
    }
  }
  
  
  /**
   * Initialize the diagram system
   * Only handles state subscription now, removed tab-specific code
   * @returns {Object} - The configured system with cleanup method
   */
  function setupDiagramSystem() {
    // Track state subscriptions for cleanup
    let stateUnsubscribe = null;
    
    // Subscribe to state changes for diagram data
    if (stateManager) {
      stateUnsubscribe = stateManager.subscribe('journeyDiagram', (newState) => {
        // Handle state changes to diagram data
        if (newState && newState.data) {
          window.journeyDiagramData = newState.data;
        }
      });
    }
    
    console.log('Diagram system initialized - all tab logic removed and moved to tab engine');
  }
  
  /**
   * Clean up resources used by the diagram system
   */
  function cleanup() {
    // Clean up all sub-modules
    renderer.cleanup();
    panZoom.cleanup();
    
    // Clean up event listeners
    events.cleanup();
  }
  
  // Initialize system
  setupDiagramSystem();
  
  // Create combined API by merging all sub-module APIs
  const api = {
    // Core functionality 
    initMermaid: renderer.initMermaid,
    renderMermaidDiagram: renderer.renderMermaidDiagram,
    renderDiagram,  // Primary diagram rendering function
    
    // System functions
    setupDiagramSystem,
    
    // Direct zoom control functions
    zoomIn: panZoom.zoomIn,
    zoomOut: panZoom.zoomOut,
    reset: panZoom.reset,
    recenterForViewport: panZoom.recenterForViewport,
    restoreOriginalCenter: panZoom.restoreOriginalCenter,
    
    // Node centering functions
    centerNodeInViewport: panZoom.centerNodeInViewport,
    animatedZoomToNode: panZoom.animatedZoomToNode,
    
    // Internal functions for testing
    initializeDiagramControls: panZoom.initializeDiagramControls,
    initializePanzoom: panZoom.initializePanzoom,
    
    // Cleanup
    cleanup
  };
  
  return api;
}